import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import './Segmenter.css';

type CanvasPoint = {
  x: number;
  y: number;
};

type BoundingBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
};

type WorkerSegment = {
  id: number;
  mask: ArrayBuffer;
  bbox: BoundingBox;
  area: number;
  score: number;
  point: CanvasPoint;
};

type WorkerResponse = {
  width: number;
  height: number;
  totalPoints: number;
  segments: WorkerSegment[];
};

type BaseSegment = {
  id: number;
  maskData: Uint8Array;
  bbox: BoundingBox;
  area: number;
  score: number;
  point: CanvasPoint;
  areaRatio: number;
};

type SegmentAsset = BaseSegment & {
  previewUrl: string;
  blob: Blob;
  isCalibration: boolean;
  defaultName: string;
  customName?: string;
};

type PromptPoint = {
  id: string;
  x: number;
  y: number;
  label: 0 | 1;
};

type PromptPreview = {
  mask: Uint8Array;
  bbox: BoundingBox;
  area: number;
  score: number;
  width: number;
  height: number;
};

type CanvasSource = ImageBitmap | HTMLImageElement;

const formatPercent = (value: number, digits = 1) => `${(value * 100).toFixed(digits)}%`;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

function loadImageSource(file: File, previewUrl: string): Promise<CanvasSource> {
  if ('createImageBitmap' in window) {
    return createImageBitmap(file).catch(
      () => new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = previewUrl;
      }),
    );
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = previewUrl;
  });
}

const createId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const sanitizeLabel = (label: string) => {
  const trimmed = label.trim();
  if (!trimmed) {
    return 'segment';
  }
  return trimmed
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .toLowerCase();
};

const Segmenter: React.FC = () => {
  const workerRef = useRef<Worker | null>(null);
  const imageSourceRef = useRef<CanvasSource | null>(null);
  const segmentPreviewUrlsRef = useRef<string[]>([]);
  const imagePreviewUrlRef = useRef<string | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [modelReady, setModelReady] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Loading SAM model…');
  const [isSegmenting, setIsSegmenting] = useState(false);
  const [segments, setSegments] = useState<SegmentAsset[]>([]);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [fileName, setFileName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPreparingDownload, setIsPreparingDownload] = useState(false);

  const [pointMode, setPointMode] = useState<'positive' | 'negative'>('positive');
  const [promptPoints, setPromptPoints] = useState<PromptPoint[]>([]);
  const [promptPreview, setPromptPreview] = useState<PromptPreview | null>(null);
  const [isDecoding, setIsDecoding] = useState(false);

  const drawMask = useCallback((maskData: Uint8Array, width: number, height: number) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    canvas.width = width;
    canvas.height = height;

    const imageData = context.createImageData(width, height);
    for (let i = 0; i < maskData.length; i += 1) {
      if (maskData[i]) {
        const offset = i * 4;
        imageData.data[offset] = 108; // red
        imageData.data[offset + 1] = 122; // green
        imageData.data[offset + 2] = 255; // blue
        imageData.data[offset + 3] = 120; // alpha
      }
    }

    context.putImageData(imageData, 0, 0);
  }, []);

  const clearMask = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext('2d');
    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  const resequenceSegments = useCallback((list: SegmentAsset[], forcedCalibrationId?: number | null) => {
    let polygonCounter = 0;
    return list.map((segment) => {
      const isCalibration = forcedCalibrationId != null
        ? segment.id === forcedCalibrationId
        : segment.isCalibration;
      const defaultName = isCalibration
        ? 'calib'
        : `Polygon-${String(++polygonCounter).padStart(2, '0')}`;
      return {
        ...segment,
        isCalibration,
        defaultName,
      };
    });
  }, []);

  const renderSegmentAssetCore = useCallback(
    async (segment: BaseSegment, dims: { width: number; height: number }) => {
      if (!imageSourceRef.current) {
        throw new Error('Original image not available for masking.');
      }

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        throw new Error('Canvas context is not supported in this browser.');
      }

      const { bbox, maskData } = segment;
      canvas.width = Math.max(1, bbox.width);
      canvas.height = Math.max(1, bbox.height);

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(
        imageSourceRef.current,
        bbox.minX,
        bbox.minY,
        bbox.width,
        bbox.height,
        0,
        0,
        bbox.width,
        bbox.height,
      );

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let y = 0; y < canvas.height; y += 1) {
        const maskRowOffset = (bbox.minY + y) * dims.width;
        for (let x = 0; x < canvas.width; x += 1) {
          const maskIndex = maskRowOffset + bbox.minX + x;
          if (!maskData[maskIndex]) {
            data[(y * canvas.width + x) * 4 + 3] = 0;
          }
        }
      }

      context.putImageData(imageData, 0, 0);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((value) => {
          if (value) {
            resolve(value);
          } else {
            reject(new Error('Failed to render segment image.'));
          }
        }, 'image/png');
      });

      const previewUrl = URL.createObjectURL(blob);
      return { blob, previewUrl };
    },
    [],
  );

  const resetInteraction = useCallback(() => {
    setPromptPoints([]);
    setPromptPreview(null);
    setIsDecoding(false);
    clearMask();
  }, [clearMask]);

  const resetSegments = useCallback(() => {
    setSegments([]);
    setErrorMessage(null);
    segmentPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    segmentPreviewUrlsRef.current = [];
    resetInteraction();
  }, [resetInteraction]);

  const handleWorkerResult = useCallback(
    (result: WorkerResponse) => {
      const { width, height } = result;
      setImageDimensions({ width, height });
      segmentPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      segmentPreviewUrlsRef.current = [];
      setSegments([]);
      setErrorMessage(null);
      resetInteraction();
      setIsSegmenting(false);
      setStatusMessage('Image ready. Add positive or negative points to generate a mask preview.');
    },
    [resetInteraction],
  );

  useEffect(() => {
    const worker = new Worker(new URL('../workers/samWorker.ts', import.meta.url), {
      type: 'module',
    });

    workerRef.current = worker;

    const handleMessage = (event: MessageEvent) => {
      const { type, data } = event.data as { type: string; data?: any };

      switch (type) {
        case 'ready':
          setModelReady(true);
          setStatusMessage('Model ready. Upload a fabric photo to begin.');
          break;
        case 'status':
          if (data?.message) {
            setStatusMessage(data.message);
          }
          break;
        case 'segment_complete':
          handleWorkerResult(data as WorkerResponse);
          break;
        case 'decode_result': {
          setIsDecoding(false);
          const maskArray = new Uint8Array(data.mask);
          if (data.area === 0) {
            setPromptPreview(null);
            clearMask();
            setStatusMessage('Prompt points did not yield a mask. Adjust the points and try again.');
            break;
          }
          const preview: PromptPreview = {
            mask: maskArray,
            bbox: data.bbox,
            area: data.area,
            score: data.score,
            width: data.width,
            height: data.height,
          };
          setPromptPreview(preview);
          drawMask(maskArray, data.width, data.height);
          setStatusMessage('Preview updated from prompt points.');
          break;
        }
        case 'error':
          setIsSegmenting(false);
          setIsDecoding(false);
          setErrorMessage(data?.message ?? 'Unexpected error while running SAM.');
          setStatusMessage('Segmentation failed.');
          break;
        default:
          break;
      }
    };

    worker.addEventListener('message', handleMessage);
    worker.postMessage({ type: 'warmup' });

    return () => {
      worker.removeEventListener('message', handleMessage);
      worker.terminate();
      workerRef.current = null;
    };
  }, [handleWorkerResult, drawMask, clearMask]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    resetSegments();

    const fileBaseName = file.name.replace(/\.[^/.]+$/, '');
    setFileName(fileBaseName);

    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = null;
    }

    const previewUrl = URL.createObjectURL(file);
    imagePreviewUrlRef.current = previewUrl;

    const imageSource = await loadImageSource(file, previewUrl);
    imageSourceRef.current = imageSource;

    const width = imageSource instanceof ImageBitmap
      ? imageSource.width
      : (imageSource as HTMLImageElement).naturalWidth || (imageSource as HTMLImageElement).width;
    const height = imageSource instanceof ImageBitmap
      ? imageSource.height
      : (imageSource as HTMLImageElement).naturalHeight || (imageSource as HTMLImageElement).height;
    setImageDimensions({ width, height });

    if (!workerRef.current) {
      setErrorMessage('Worker is not ready. Please reload the page.');
      return;
    }

    workerRef.current.postMessage({ type: 'reset' });

    setStatusMessage('Running SAM to compute image embeddings…');
    setIsSegmenting(true);

    workerRef.current.postMessage({
      type: 'segment',
      data: { file },
    });
  };

  const requestDecode = useCallback((points: PromptPoint[]) => {
    if (!workerRef.current || !points.length) {
      return;
    }
    setIsDecoding(true);
    workerRef.current.postMessage({
      type: 'decode',
      data: {
        points: points.map(({ x, y, label }) => ({ x, y, label })),
      },
    });
    setStatusMessage('Generating mask from prompt points…');
  }, []);

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (!imageRef.current || !imageDimensions || isSegmenting) {
      return;
    }

    const rect = imageRef.current.getBoundingClientRect();
    const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
    const label: 0 | 1 = pointMode === 'positive' ? 1 : 0;

    const nextPoints = [...promptPoints, { id: createId(), x, y, label }];
    setPromptPoints(nextPoints);
    requestDecode(nextPoints);
  };

  const handleUndoPoint = () => {
    if (!promptPoints.length) {
      return;
    }
    const nextPoints = promptPoints.slice(0, -1);
    setPromptPoints(nextPoints);
    if (nextPoints.length) {
      requestDecode(nextPoints);
    } else {
      setPromptPreview(null);
      clearMask();
      setIsDecoding(false);
      setStatusMessage('All prompt points removed.');
    }
  };

  const handleClearPoints = () => {
    setPromptPoints([]);
    setPromptPreview(null);
    setIsDecoding(false);
    clearMask();
    setStatusMessage('Prompt points cleared.');
  };

  const addPreviewAsSegment = useCallback(
    async (markCalibration: boolean) => {
      if (!promptPreview || !imageDimensions) {
        return;
      }

      const firstPoint = promptPoints[0];
      const referencePoint: CanvasPoint = firstPoint
        ? {
          x: firstPoint.x * imageDimensions.width,
          y: firstPoint.y * imageDimensions.height,
        }
        : {
          x: promptPreview.bbox.minX,
          y: promptPreview.bbox.minY,
        };

      const maskCopy = new Uint8Array(promptPreview.mask);

      const baseSegment: BaseSegment = {
        id: Date.now(),
        maskData: maskCopy,
        bbox: promptPreview.bbox,
        area: promptPreview.area,
        score: promptPreview.score,
        point: referencePoint,
        areaRatio: imageDimensions.width && imageDimensions.height
          ? promptPreview.area / (imageDimensions.width * imageDimensions.height)
          : 0,
      };

      const { blob, previewUrl } = await renderSegmentAssetCore(baseSegment, imageDimensions);

      setSegments((prev) => {
        const cleared = markCalibration
          ? prev.map((segment) => ({ ...segment, isCalibration: false }))
          : prev;
        const nextList: SegmentAsset[] = [
          ...cleared,
          {
            ...baseSegment,
            blob,
            previewUrl,
            isCalibration: markCalibration,
            defaultName: 'temp',
          },
        ];

        const resequenced = resequenceSegments(nextList, markCalibration ? baseSegment.id : undefined);
        segmentPreviewUrlsRef.current = resequenced.map((segment) => segment.previewUrl);
        return resequenced;
      });

      setStatusMessage(markCalibration
        ? 'Marked calibration segment from prompt preview.'
        : 'Added fabric segment from prompt preview.');

      resetInteraction();
    },
    [promptPreview, promptPoints, imageDimensions, renderSegmentAssetCore, resequenceSegments, resetInteraction],
  );

  const handleNameChange = (id: number, value: string) => {
    setSegments((prev) => {
      const updated = prev.map((segment) => (
        segment.id === id
          ? { ...segment, customName: value }
          : segment
      ));
      return updated;
    });
  };

  const handleSelectCalibration = (id: number) => {
    setSegments((prev) => {
      const resequenced = resequenceSegments(prev, id);
      segmentPreviewUrlsRef.current = resequenced.map((segment) => segment.previewUrl);
      return resequenced;
    });
    setStatusMessage('Calibration segment updated.');
  };

  const handleRemoveSegment = useCallback(
    (id: number) => {
      let didRemove = false;
      setSegments((prev) => {
        const target = prev.find((segment) => segment.id === id);
        if (!target) {
          return prev;
        }
        didRemove = true;
        URL.revokeObjectURL(target.previewUrl);
        const remaining = prev.filter((segment) => segment.id !== id);
        const resequenced = resequenceSegments(remaining);
        segmentPreviewUrlsRef.current = resequenced.map((segment) => segment.previewUrl);
        return resequenced;
      });
      if (didRemove) {
        setStatusMessage('Segment removed.');
      }
    },
    [resequenceSegments],
  );

  const calibrationSegment = useMemo(
    () => segments.find((segment) => segment.isCalibration) ?? null,
    [segments],
  );

  const fabricSegments = useMemo(
    () => segments.filter((segment) => !segment.isCalibration),
    [segments],
  );

  const handleDownload = async () => {
    if (!segments.length || !fileName) {
      return;
    }

    setIsPreparingDownload(true);

    try {
      const zip = new JSZip();
      const usedNames = new Map<string, number>();
      const metadata: Array<{
        id: number;
        isCalibration: boolean;
        score: number;
        area: number;
        areaRatio: number;
        bbox: BoundingBox;
        point: CanvasPoint;
        exportName: string;
      }> = [];

      const addFile = (baseLabel: string, blob: Blob) => {
        const sanitized = sanitizeLabel(baseLabel);
        const count = usedNames.get(sanitized) ?? 0;
        usedNames.set(sanitized, count + 1);
        const unique = count === 0 ? sanitized : `${sanitized}_${count + 1}`;
        zip.file(`${fileName}_${unique}.png`, blob);
        return unique;
      };

      if (calibrationSegment) {
        const exportName = addFile('calib', calibrationSegment.blob);
        metadata.push({
          id: calibrationSegment.id,
          isCalibration: true,
          score: Number(calibrationSegment.score.toFixed(4)),
          area: calibrationSegment.area,
          areaRatio: Number(calibrationSegment.areaRatio.toFixed(6)),
          bbox: calibrationSegment.bbox,
          point: calibrationSegment.point,
          exportName,
        });
      }

      let fabricIndex = 0;
      segments.forEach((segment) => {
        if (segment.isCalibration) {
          return;
        }
        const fallback = `Polygon-${String(++fabricIndex).padStart(2, '0')}`;
        const labelSource = segment.customName && segment.customName.trim()
          ? segment.customName
          : segment.defaultName || fallback;
        const exportName = addFile(labelSource, segment.blob);
        metadata.push({
          id: segment.id,
          isCalibration: false,
          score: Number(segment.score.toFixed(4)),
          area: segment.area,
          areaRatio: Number(segment.areaRatio.toFixed(6)),
          bbox: segment.bbox,
          point: segment.point,
          exportName,
        });
      });

      zip.file(`${fileName}_segments.json`, JSON.stringify(metadata, null, 2));

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${fileName}_segments.zip`);
    } finally {
      setIsPreparingDownload(false);
    }
  };

  useEffect(() => () => {
    segmentPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = null;
    }
  }, []);

  const disabled = !modelReady || isSegmenting;

  return (
    <div className="segmenter">
      <header className="segmenter__header">
        <h1>Segment Fabric Pieces with SAM</h1>
        <p>
          Upload a fabric photo, click positive/negative points to refine masks, mark the calibration square, and download polished cut-outs as a ZIP archive.
          This app references <a href="https://huggingface.co/spaces/Xenova/segment-anything-web" target="_blank" rel="noopener noreferrer">Xenova's Segment Anything Web</a> for the Segment Anything Model (SAM) and is coded by GPT-5 Codex.
        </p>
      </header>

      <section className="segmenter__uploader">
        <label className={`segmenter__dropzone ${disabled ? 'segmenter__dropzone--disabled' : ''}`}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={disabled}
            hidden
          />
          <span>{disabled ? 'Loading…' : 'Click or drag an image to start'}</span>
        </label>
      </section>

      <section className="segmenter__status">
        <p className="segmenter__status-text">{statusMessage}</p>
        {errorMessage && <p className="segmenter__error">{errorMessage}</p>}
      </section>

      {imagePreviewUrlRef.current && imageDimensions && (
        <section className="segmenter__preview">
          <div
            className="segmenter__preview-stage"
            onContextMenu={(event) => event.preventDefault()}
          >
            <img
              ref={imageRef}
              src={imagePreviewUrlRef.current}
              alt="Fabric upload preview"
              onClick={handleImageClick}
            />
            <canvas ref={overlayCanvasRef} className="segmenter__mask-canvas" />
            {promptPoints.map((point) => (
              <span
                key={point.id}
                className={`segmenter__point segmenter__point--${point.label === 1 ? 'positive' : 'negative'}`}
                style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
              />
            ))}
          </div>
          <p className="segmenter__preview-meta">
            {imageDimensions.width}×{imageDimensions.height}px
          </p>
          <div className="segmenter__prompt-toolbar">
            <div className="segmenter__toggle-group">
              <button
                type="button"
                className={`segmenter__toggle ${pointMode === 'positive' ? 'segmenter__toggle--active' : ''}`}
                onClick={() => setPointMode('positive')}
              >
                Positive point
              </button>
              <button
                type="button"
                className={`segmenter__toggle ${pointMode === 'negative' ? 'segmenter__toggle--active' : ''}`}
                onClick={() => setPointMode('negative')}
              >
                Negative point
              </button>
            </div>
            <div className="segmenter__prompt-actions">
              <button
                type="button"
                onClick={handleUndoPoint}
                disabled={!promptPoints.length}
              >
                Undo point
              </button>
              <button
                type="button"
                onClick={handleClearPoints}
                disabled={!promptPoints.length}
              >
                Clear points
              </button>
            </div>
          </div>
          {promptPreview && (
            <div className="segmenter__prompt-preview">
              <div className="segmenter__prompt-metrics">
                <span>score {promptPreview.score.toFixed(2)}</span>
                <span>area {formatPercent(promptPreview.area / (promptPreview.width * promptPreview.height))}</span>
              </div>
              <div className="segmenter__prompt-buttons">
                <button
                  type="button"
                  onClick={() => addPreviewAsSegment(false)}
                  disabled={isDecoding}
                >
                  Add as fabric
                </button>
                <button
                  type="button"
                  onClick={() => addPreviewAsSegment(true)}
                  disabled={isDecoding}
                >
                  Mark as calibration
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {segments.length > 0 && (
        <section className="segmenter__results">
          <div className="segmenter__summary">
            <h2>Detected Segments</h2>
            <ul>
              <li>
                <strong>Calibration</strong> – {calibrationSegment ? 'Selected' : 'Not selected'}
              </li>
              <li>
                <strong>Fabric Pieces</strong> – {fabricSegments.length} item{fabricSegments.length === 1 ? '' : 's'}
              </li>
            </ul>
            <button
              type="button"
              className="segmenter__download"
              onClick={handleDownload}
              disabled={isPreparingDownload || segments.length === 0}
            >
              {isPreparingDownload ? 'Preparing ZIP…' : 'Download segmented ZIP'}
            </button>
          </div>

          <div className="segmenter__grid">
            {segments.map((segment) => (
              <figure key={segment.id} className="segmenter__card">
                <img src={segment.previewUrl} alt={segment.isCalibration ? 'Calibration segment' : 'Fabric segment'} />
                <figcaption>
                  <div className="segmenter__card-header">
                    <span className={`segmenter__badge ${segment.isCalibration ? 'segmenter__badge--calib' : ''}`}>
                      {segment.isCalibration ? 'Calibration' : 'Fabric piece'}
                    </span>
                    <div className="segmenter__card-controls">
                      <label className="segmenter__calib-control">
                        <input
                          type="radio"
                          name="calibration"
                          checked={segment.isCalibration}
                          onChange={() => handleSelectCalibration(segment.id)}
                        />
                        <span>Calib.</span>
                      </label>
                      <button
                        type="button"
                        className="segmenter__delete-button"
                        onClick={() => handleRemoveSegment(segment.id)}
                        aria-label="Delete segment"
                      >
                        Del
                      </button>
                    </div>
                  </div>
                  <input
                    className="segmenter__name-input"
                    value={segment.customName ?? segment.defaultName}
                    onChange={(event) => handleNameChange(segment.id, event.target.value)}
                    placeholder={segment.isCalibration ? 'calib' : segment.defaultName}
                  />
                  <div className="segmenter__card-metrics">
                    <span>score {segment.score.toFixed(2)}</span>
                    <span>area {formatPercent(segment.areaRatio)}</span>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Segmenter;


